import { forwardRef } from 'react';
import type { QuoteDetail } from '@lib/api/quotes';

interface PrintableQuoteProps {
  quote: QuoteDetail;
  companyInfo?: {
    name: string;
    address: string;
    phone: string;
    email: string;
    logo?: string;
  };
  quoteFooter?: string;
}

export const PrintableQuote = forwardRef<HTMLDivElement, PrintableQuoteProps>(
  ({ quote, companyInfo, quoteFooter }, ref) => {
    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: 'CAD',
      }).format(amount);
    };

    const formatDate = (dateStr: string) => {
      if (!dateStr) return 'N/A';
      return new Date(dateStr).toLocaleDateString('en-CA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    };

    const getStatusLabel = (status: string) => {
      return status.toUpperCase();
    };

    // Default company info
    const company = companyInfo || {
      name: 'OchrePoint Contracting',
      address: '123 Business Ave, Suite 100\nCity, BC V8W 1A1',
      phone: '(250) 555-1234',
      email: 'info@ochrepoint.ca',
    };

    // Traditional black-on-white print styles
    const colors = {
      black: '#000000',
      border: '#000000',
      lightBorder: '#cccccc',
    };

    return (
      <div
        ref={ref}
        style={{
          width: '8.5in',
          minHeight: '11in',
          padding: '0.5in',
          backgroundColor: '#ffffff',
          fontFamily: 'Times New Roman, serif',
          fontSize: '11pt',
          color: colors.black,
          boxSizing: 'border-box',
          lineHeight: '1.4',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', borderBottom: `2px solid ${colors.black}`, paddingBottom: '20px' }}>
          {/* Company Info */}
          <div>
            {company.logo && (
              <img
                src={company.logo}
                alt={company.name}
                style={{
                  maxHeight: '50px',
                  maxWidth: '180px',
                  marginBottom: '10px',
                  objectFit: 'contain',
                }}
              />
            )}
            <div style={{ fontSize: '18pt', fontWeight: 'bold', color: colors.black, marginBottom: '6px' }}>
              {company.name}
            </div>
            <div style={{ whiteSpace: 'pre-line', color: colors.black, lineHeight: '1.5' }}>
              {company.address}
            </div>
            <div style={{ marginTop: '6px', color: colors.black }}>
              <div>Tel: {company.phone}</div>
              <div>Email: {company.email}</div>
            </div>
          </div>

          {/* Quote Title */}
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '28pt', fontWeight: 'bold', color: colors.black, marginBottom: '8px' }}>
              ESTIMATE
            </div>
            <div style={{ fontSize: '14pt', fontWeight: 'bold', color: colors.black }}>
              {quote.quote_number}
            </div>
            <div style={{ marginTop: '10px', fontSize: '10pt', color: colors.black, fontWeight: 'bold' }}>
              Status: {getStatusLabel(quote.status)}
            </div>
          </div>
        </div>

        {/* Client & Quote Details */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px' }}>
          {/* Client Info */}
          <div style={{ flex: '1' }}>
            <div style={{ fontSize: '9pt', fontWeight: 'bold', color: colors.black, marginBottom: '6px', textTransform: 'uppercase' }}>
              Prepared For
            </div>
            <div style={{ fontWeight: 'bold', fontSize: '11pt', marginBottom: '4px', color: colors.black }}>
              {quote.client_name}
            </div>
            {quote.client_company && (
              <div style={{ color: colors.black }}>{quote.client_company}</div>
            )}
            {quote.client_address && (
              <div style={{ color: colors.black, whiteSpace: 'pre-line', marginTop: '4px' }}>
                {quote.client_address}
              </div>
            )}
            <div style={{ marginTop: '6px', color: colors.black }}>
              {quote.client_email && <div>{quote.client_email}</div>}
              {quote.client_phone && <div>{quote.client_phone}</div>}
            </div>
          </div>

          {/* Quote Details */}
          <div style={{ textAlign: 'right' }}>
            <table style={{ marginLeft: 'auto', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '3px 12px 3px 0', color: colors.black, textAlign: 'right', fontWeight: 'bold' }}>Date:</td>
                  <td style={{ padding: '3px 0', color: colors.black }}>{formatDate(quote.created_at)}</td>
                </tr>
                {quote.valid_until && (
                  <tr>
                    <td style={{ padding: '3px 12px 3px 0', color: colors.black, textAlign: 'right', fontWeight: 'bold' }}>Valid Until:</td>
                    <td style={{ padding: '3px 0', color: colors.black }}>{formatDate(quote.valid_until)}</td>
                  </tr>
                )}
                {quote.job_title && (
                  <tr>
                    <td style={{ padding: '3px 12px 3px 0', color: colors.black, textAlign: 'right', fontWeight: 'bold' }}>Project:</td>
                    <td style={{ padding: '3px 0', color: colors.black }}>{quote.job_title}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Introduction / Notes */}
        {quote.notes && (
          <div style={{ marginBottom: '30px', fontStyle: 'italic' }}>
            {quote.notes}
          </div>
        )}

        {/* Sections and Items */}
        {quote.sections.map((section) => {
          const sectionItems = quote.items.filter(i => i.section_id === section.id);
          if (sectionItems.length === 0) return null;

          return (
            <div key={section.id} style={{ marginBottom: '25px' }}>
              <div style={{ 
                fontSize: '12pt', 
                fontWeight: 'bold', 
                backgroundColor: '#f5f5f5', 
                padding: '6px 8px', 
                borderBottom: `1px solid ${colors.black}`,
                marginBottom: '10px'
              }}>
                {section.name}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors.lightBorder}` }}>
                    <th style={{ padding: '8px', textAlign: 'left', fontSize: '10pt', width: '50%' }}>Description</th>
                    <th style={{ padding: '8px', textAlign: 'right', fontSize: '10pt', width: '10%' }}>Qty</th>
                    <th style={{ padding: '8px', textAlign: 'center', fontSize: '10pt', width: '10%' }}>Unit</th>
                    <th style={{ padding: '8px', textAlign: 'right', fontSize: '10pt', width: '15%' }}>Price</th>
                    <th style={{ padding: '8px', textAlign: 'right', fontSize: '10pt', width: '15%' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {sectionItems.map((item) => (
                    <tr key={item.id} style={{ borderBottom: `1px solid #eeeeee` }}>
                      <td style={{ padding: '8px', fontSize: '10pt' }}>
                        <div style={{ fontWeight: 'bold' }}>{item.item_name}</div>
                        {item.description && <div style={{ fontSize: '9pt', color: '#444444', marginTop: '2px' }}>{item.description}</div>}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right', fontSize: '10pt' }}>{item.quantity}</td>
                      <td style={{ padding: '8px', textAlign: 'center', fontSize: '10pt' }}>{item.unit || '-'}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontSize: '10pt' }}>{formatCurrency(item.unit_price)}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontSize: '10pt', fontWeight: 'bold' }}>{formatCurrency(item.total)}</td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: '#fafafa' }}>
                    <td colSpan={4} style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: '10pt' }}>
                      Section Subtotal:
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: '10pt' }}>
                      {formatCurrency(section.subtotal)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })}

        {/* Totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '40px', pageBreakInside: 'avoid' }}>
          <table style={{ width: '280px', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ padding: '6px 12px', textAlign: 'right' }}>Subtotal:</td>
                <td style={{ padding: '6px 0', textAlign: 'right' }}>{formatCurrency(quote.subtotal)}</td>
              </tr>
              <tr>
                <td style={{ padding: '6px 12px', textAlign: 'right' }}>Tax ({Number(quote.tax_rate ?? 0)}%):</td>
                <td style={{ padding: '6px 0', textAlign: 'right' }}>{formatCurrency(quote.tax_amount)}</td>
              </tr>
              <tr style={{ borderTop: `2px solid ${colors.black}` }}>
                <td style={{ padding: '10px 12px', fontWeight: 'bold', fontSize: '12pt', textAlign: 'right' }}>Total Estimate:</td>
                <td style={{ padding: '10px 0', textAlign: 'right', fontWeight: 'bold', fontSize: '12pt' }}>
                  {formatCurrency(quote.total)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Terms and Conditions */}
        {quote.terms && (
          <div style={{ marginTop: '50px', pageBreakInside: 'avoid' }}>
            <div style={{ fontSize: '10pt', fontWeight: 'bold', marginBottom: '8px', borderBottom: `1px solid ${colors.black}` }}>
              Terms & Conditions
            </div>
            <div style={{ fontSize: '9pt', whiteSpace: 'pre-line' }}>
              {quote.terms}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ 
          marginTop: 'auto', 
          paddingTop: '30px', 
          textAlign: 'center', 
          color: '#666666', 
          fontSize: '9pt',
          borderTop: `1px solid ${colors.lightBorder}` 
        }}>
          {quoteFooter || (
            <>
              <div>Thank you for the opportunity to provide this estimate.</div>
              <div>Please contact us if you have any questions.</div>
            </>
          )}
        </div>
      </div>
    );
  }
);

PrintableQuote.displayName = 'PrintableQuote';
